/**
 * Canonical "is this association frozen?" predicate.
 *
 * The association peer of `value_update_access` on properties (see
 * `propertyEditability.ts`): `value_removal_access: 'none'` marks an
 * association write-once. The backend permits the FIRST add and then rejects
 * every removal and re-point via AssociationImmutabilityValidator, returning
 * `error_type: association_immutable`.
 *
 * "Frozen" is therefore a function of the schema AND the current value, not of
 * the schema alone:
 * - no value yet → editable, because the first add is exactly what the schema
 *   allows (this is the mint path for a service-account credential);
 * - value present → frozen, because any change the UI could offer is one the
 *   backend will reject.
 *
 * Surfaces must render a frozen association READ-ONLY rather than hiding it —
 * "Created by" is information worth showing, it just isn't information the user
 * may change. That mirrors the existing treatment of internal-write
 * associations in `overview-edit-section.tsx`, which stay visible when they
 * hold a value.
 *
 * This is deliberately NOT the write-access question. `value_write_access`
 * asks "may a user ever write this?"; each surface already answers that with
 * its own gate (and they differ in strictness on purpose — the list page's
 * inline editor fails closed on an absent field, the record overview does not).
 * This predicate answers only "is this one frozen now?", so adding it to a
 * surface can never loosen that surface's existing rule.
 */

/** The schema field the predicate reads. Structurally satisfied by
 *  `SchemaClient.AssociationType`. */
export interface AssociationRemovalAccess {
  value_removal_access?: 'all' | 'none';
}

/**
 * Whether the schema freezes this association at its current value.
 *
 * @param assocType - the association schema
 * @param hasExistingValue - whether the entity already has a target linked
 *   through this association
 */
export function isAssociationValueFrozen(
  assocType: AssociationRemovalAccess,
  hasExistingValue: boolean
): boolean {
  return assocType.value_removal_access === 'none' && hasExistingValue;
}

/** Whether a value read off an entity counts as "already linked". Empty arrays
 *  and nulls do not. */
export function hasAssociationValue(
  value: string | string[] | null | undefined
): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  return Array.isArray(value) ? value.length > 0 : value !== '';
}
